/**
 * Helpers purs ( zero dependance Vue / Vue-i18n / services REST ) pour
 * la gestion des droits {@code applicationManager} et {@code userManager} .
 *
 * <p>Le fichier voisin {@code useRoleManagementChanges.ts} est un thin
 * wrapper qui injecte la reactivite Vue + i18n + services au-dessus de
 * ces fonctions . Toute la logique testable unitairement vit ici .
 *
 * <p><b>Hierarchie metier ( miroir des roles SQL Postgres ) :</b>
 * <ul>
 *   <li>{@code applicationManager} IMPLIQUE {@code userManager} - cf
 *       {@code SchemaFlywayCallback} qui execute en migration
 *       {@code GRANT userManager TO applicationManager} .</li>
 *   <li>{@code userManager} peut etre accorde / retire seul .</li>
 *   <li>Un applicationManager non-{@code openAdomAdmin} ne peut pas
 *       s'auto-revoquer ( garde-fou serveur {@code assertNotSelfRevoke}
 *       + {@code CantSelfRevokeApplicationRoleException} ) .</li>
 * </ul>
 */

/**
 * Roles de gestion gerables depuis cet ecran . L'ordre du tuple est
 * utilise lors de l'iteration des appels REST .
 */
export const MANAGEMENT_ROLES = ["applicationManager", "userManager"] as const;
export type ManagementRole = typeof MANAGEMENT_ROLES[number];

export interface RoleChangeBucket {
  add: string[];
  remove: string[];
}

export type RoleChangeBuckets = Record<ManagementRole, RoleChangeBucket>;

export interface UserRow {
  id?: string;
  isApplicationManager?: boolean;
  isUserManager?: boolean;
}

export interface CurrentUserView {
  id?: string;
  openAdomAdmin?: boolean;
}

/**
 * Cles i18n centralisees pour les tooltips dynamiques . Centraliser ici
 * evite la dispersion de litteraux dans le composable et permet le
 * renommage / la traduction sans toucher la logique pure .
 */
export const TOOLTIP_KEYS = {
  cantSelfRevoke: "dataTypeAuthorizations.cant-self-revoke-tooltip",
  userManagerImplied: "dataTypeAuthorizations.user-manager-implied-tooltip",
} as const;

export function createEmptyBuckets(): RoleChangeBuckets {
  return {
    applicationManager: { add: [], remove: [] },
    userManager: { add: [], remove: [] },
  };
}

/**
 * Enregistre une modification ( cochage ou decochage ) dans les buckets
 * {@code add} / {@code remove} du role donne . Idempotent : les deux
 * listes restent disjointes peu importe le nombre d'appels successifs .
 */
export function trackChange(
  buckets: RoleChangeBuckets,
  role: ManagementRole,
  value: boolean,
  userId: string,
): void {
  const bucket = buckets[role];
  if (value) {
    bucket.remove = bucket.remove.filter(id => id !== userId);
    if (!bucket.add.includes(userId)) bucket.add.push(userId);
  }
  else {
    bucket.add = bucket.add.filter(id => id !== userId);
    if (!bucket.remove.includes(userId)) bucket.remove.push(userId);
  }
}

export function isSelfRevokeBlocked(row: UserRow, currentUser: CurrentUserView): boolean {
  return !!row?.id
    && row.id === currentUser?.id
    && !currentUser?.openAdomAdmin;
}

export function isApplicationManagerDisabled(row: UserRow, currentUser: CurrentUserView): boolean {
  return isSelfRevokeBlocked(row, currentUser);
}

/**
 * Vrai si la case userManager doit etre grisee : self-revoke bloque OU
 * heritage automatique par {@code applicationManager} ( cocher l'app
 * implique de facto userManager au niveau Postgres ) .
 */
export function isUserManagerDisabled(row: UserRow, currentUser: CurrentUserView): boolean {
  return isSelfRevokeBlocked(row, currentUser) || !!row?.isApplicationManager;
}

/**
 * Cle i18n pour le tooltip de la case applicationManager ; null si pas
 * de message a afficher ( case active sans contrainte ) .
 */
export function applicationManagerTooltipKey(row: UserRow, currentUser: CurrentUserView): string | null {
  return isSelfRevokeBlocked(row, currentUser) ? TOOLTIP_KEYS.cantSelfRevoke : null;
}

/**
 * Cle i18n pour le tooltip de la case userManager ; null si pas de
 * message a afficher .
 */
export function userManagerTooltipKey(row: UserRow, currentUser: CurrentUserView): string | null {
  if (isSelfRevokeBlocked(row, currentUser)) return TOOLTIP_KEYS.cantSelfRevoke;
  if (row?.isApplicationManager) return TOOLTIP_KEYS.userManagerImplied;
  return null;
}
